import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { devicesAPI } from '../utils/api';

/**
 * WebToNativeBridge (iter67)
 *
 * Wires the WebToNative JavaScript bridge (window.WTN.Firebase.Messaging) into our
 * backend so that Android/iOS apps wrapped via webtonative.com can:
 *   1. Retrieve the device's FCM token on app launch (after the user logs in)
 *   2. Register that token with /api/devices/register so the backend can target it
 *   3. Subscribe to a per-user FCM topic (`user_<user_id>`) for an alternative
 *      topic-based delivery channel
 *   4. Tap notifications and be deep-linked into the right route (e.g. /chat/<id>)
 *
 * Detection strategy:
 *   - We only run if `window.WTN?.Firebase?.Messaging` is present — this is true
 *     ONLY inside the WebToNative wrapper. On regular browsers this is a no-op.
 *   - We re-attempt token retrieval after a 2s grace period because the WTN
 *     bridge can lag a beat after initial page paint on some devices.
 *
 * Docs: https://docs.webtonative.com/javascript-apis/google-firebase-notification
 */
const WebToNativeBridge = ({ children }) => {
  const { user } = useAuth();
  const registeredTokenRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Only proceed when:
    //   (a) running inside WebToNative (window.WTN injected by their wrapper), AND
    //   (b) the user is logged in (so we can attribute the token to a user_id)
    if (!user?.user_id) return;

    const tryRegister = () => {
      const WTN = window.WTN;
      const Messaging = WTN?.Firebase?.Messaging;
      if (!Messaging?.getFCMToken) return false;

      Messaging.getFCMToken({
        callback: async (data) => {
          const token = data?.token;
          if (!token) return;
          if (registeredTokenRef.current === token) return; // already registered
          registeredTokenRef.current = token;

          // Best-effort platform detection from UA. WebToNative sets a custom UA
          // ("WTN-Android" / "WTN-iOS") on most builds; fall back to UA sniff.
          const ua = (navigator.userAgent || '').toLowerCase();
          const platform = ua.includes('android') ? 'android'
                         : (ua.includes('iphone') || ua.includes('ipad')) ? 'ios'
                         : 'unknown';

          try {
            await devicesAPI.register(token, platform);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('FCM device register failed:', err?.response?.status, err?.message);
          }

          // Subscribe to a personal topic for per-user fan-out
          try {
            Messaging.subscribe?.({ toTopic: `user_${user.user_id}` });
          } catch (_e) { /* topic subscription is optional */ }
        },
      });
      return true;
    };

    // Try immediately; if the bridge isn't ready yet, retry after 2s
    if (!tryRegister()) {
      const t = setTimeout(tryRegister, 2000);
      return () => clearTimeout(t);
    }
  }, [user]);

  return children;
};

export default WebToNativeBridge;
